// Rejected goods step 3b (2026-10-25; approved mockup AUMPidbNn2tJqJ4ujXtcPv, "Payments & ADIB" 5–6): the two questions a bank line
// can need. "What was this payment?" — a purchase for a PO (pick it now, or the PO is coming), another company expense (kind, optional
// cost center, what it was + the receipt), personal or by mistake (Walid owes it back), a bank fee; for money in: a vendor refund,
// Walid paying back, a top-up, something else. "Explain the difference" — a bank or InstaPay charge, it also paid another PO, I recorded
// the wrong amount (corrected; the first entry is kept), something else (goes to Ahmed). Saved by purchasing-account.service.
// 2026-10-26 (Ahmed's B1/B3): the exact InstaPay charge never reaches this window (booked as a fee by itself); a correction is to the
// bank amount less that charge; a line that took less than its payment offers "Paid in more than one transfer" (pick the other line).
import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Select, message } from 'antd';
import { BankRow, explainBankDifference, getOpenRefunds, getSplitCandidates, getVendorPos, ProofFile, resolveBankLine, searchPosForPayment, SplitCandidates, uploadPaymentProof, VendorPo, withoutInstapayCharge } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import CostCenterSelect from '../CostCenterSelect';
import { money, OutcomeCard, ProofPicker } from '../receiving/shared';

const text: React.CSSProperties = { fontSize: 12.5, color: '#5f6672' };
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5f6672', minWidth: 0 };
const req = <span style={{ color: '#cf1322' }}>* </span>;
const EXPENSES = [
  { value: 'transport', label: 'Transport and delivery' }, { value: 'bills', label: 'Bills and utilities' }, { value: 'government', label: 'Government and fees' },
  { value: 'office', label: 'Office and hospitality' }, { value: 'other', label: 'Other' },
];
const head = (l: BankRow) => `${new Date(`${l.date}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })} · "${l.narrative}" · EGP ${money(l.debit || l.credit)}`;

/** A PO to record the payment on: search by PO number or vendor. */
export const PoPicker: React.FC<{ value: string | null; onChange: (poId: string | null) => void }> = ({ value, onChange }) => {
  const [opts, setOpts] = useState<Array<{ value: string; label: string }>>([]);
  const search = async (q: string) => {
    try {
      const res = await searchPosForPayment(q);
      setOpts(res.map((p) => ({ value: p.poId, label: `${p.poId} · ${p.vendor?.name || ''} · ${p.status}` })));
    } catch { setOpts([]); }
  };
  useEffect(() => { search(''); }, []);
  return <Select showSearch allowClear filterOption={false} onSearch={search} value={value ?? undefined} onChange={(v) => onChange(v ?? null)} options={opts} placeholder="PO number or vendor" style={{ width: '100%' }} />;
};

export const WhatWasThisWindow: React.FC<{ line: BankRow | null; onClose: () => void; onSaved: () => void; onRecordOnPo: (line: BankRow, poId: string) => void }> = ({ line, onClose, onSaved, onRecordOnPo }) => {
  const compact = useCompactLayout();
  const [as, setAs] = useState<string | null>(null);
  const [poId, setPoId] = useState<string | null>(null);
  const [coming, setComing] = useState(false);
  const [kind, setKind] = useState<string | null>(null);
  const [cc, setCc] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [proof, setProof] = useState<ProofFile[]>([]);
  const [refunds, setRefunds] = useState<Array<{ id: number; on: string; amount: number; reference: string | null }>>([]);
  const [refundId, setRefundId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!line) return;
    setAs(null); setPoId(null); setComing(false); setKind(null); setCc(null); setNote(''); setProof([]); setRefundId(null);
    if (line.credit > 0) getOpenRefunds().then(setRefunds).catch(() => setRefunds([]));
  }, [line]);
  if (!line) return null;
  const out = line.debit > 0;
  const options = out
    ? [
      { value: 'po', label: 'A purchase for a PO', hint: 'Pick the PO now, or "the PO is coming": the line stays on This week until the PO exists and is linked.' },
      { value: 'outside', label: 'Another company expense', hint: 'Not a purchase. It goes on the settlement under "Paid outside purchasing" for finance.' },
      { value: 'personal', label: 'Personal, or paid by mistake', hint: 'The officer owes it back. It stays open until the repayment shows on the statement.' },
      { value: 'fee', label: 'A bank fee or charge', hint: 'Booked as a bank fee for the week.' },
      // Step 5e (item 16): a purchase paid from the officer's own money is repaid from the account.
      { value: 'officer_repaid', label: 'Repaying the officer', hint: 'For purchases the officer paid with their own money. It lowers "We owe the officer" on Officer\'s cash.' },
    ]
    : [
      { value: 'refund_in', label: 'A refund from a vendor', hint: 'Pick the refund recorded on Returns & credits.' },
      { value: 'repayment', label: 'The officer paying back', hint: 'A personal or mistaken payment the officer returned.' },
      { value: 'top_up', label: 'A top-up', hint: 'Money put into the account for purchasing.' },
      { value: 'other_in', label: 'Something else', hint: 'Write what it was; it goes to Ahmed on the settlement.' },
    ];
  const ok = !!as && (
    as === 'po' ? (coming ? !!note.trim() : !!poId)
      : as === 'outside' ? !!kind && !!note.trim() && proof.length > 0
        : as === 'personal' || as === 'other_in' ? !!note.trim()
          : as === 'refund_in' ? !!refundId : true
  );
  const save = async () => {
    if (!as) return;
    if (as === 'po' && !coming && poId) { onRecordOnPo(line, poId); return; }
    setSaving(true);
    try {
      await resolveBankLine(line.id, {
        as: as === 'po' ? 'po_coming' : as, expenseKind: as === 'outside' ? kind ?? undefined : undefined, costCenterId: as === 'outside' ? cc : undefined,
        note: note.trim() || undefined, proofFiles: as === 'outside' ? proof : undefined, settlementId: as === 'refund_in' ? refundId ?? undefined : undefined,
      });
      message.success('Saved');
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open={!!line} title={<div style={{ paddingInlineEnd: 28 }}>{out ? 'What was this payment?' : 'What was this money?'} · {head(line)}</div>}
      onCancel={onClose} onOk={save} okText={as === 'po' && !coming ? 'Record the payment on this PO' : 'Save'} okButtonProps={{ disabled: !ok, loading: saving }} maskClosable={false} width={compact ? 520 : 680} destroyOnClose>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {options.map((o) => (
            <OutcomeCard key={o.value} label={o.label} on={as === o.value} onPick={() => setAs(o.value)}><div style={text}>{o.hint}</div></OutcomeCard>
          ))}
        </div>
        {as === 'po' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!coming && <label style={lab}><span>{req}The PO</span><PoPicker value={poId} onChange={setPoId} /></label>}
            <label style={{ fontSize: 13 }}><input type="checkbox" checked={coming} onChange={(e) => setComing(e.target.checked)} /> The PO is not in the ERP yet (it is coming)</label>
            {coming && <label style={lab}><span>{req}What was bought, from whom</span><Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></label>}
          </div>
        )}
        {as === 'outside' && (
          <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <label style={lab}><span>{req}What kind of expense</span><Select value={kind ?? undefined} onChange={setKind} options={EXPENSES} placeholder="Choose" /></label>
            <label style={lab}><span>Job or department (optional)</span><CostCenterSelect value={cc} onChange={(id) => setCc(id)} placeholder="Choose a cost center" /></label>
            <label style={{ ...lab, gridColumn: '1 / -1' }}><span>{req}What it was</span><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Workshop land-line bill, October" maxLength={500} /></label>
            <div style={{ ...lab, gridColumn: '1 / -1' }}><span>{req}The receipt</span><ProofPicker value={proof} onChange={setProof} upload={uploadPaymentProof} label="Add the bill or a screenshot" /></div>
          </div>
        )}
        {(as === 'personal' || as === 'other_in') && <label style={lab}><span>{req}{as === 'personal' ? 'What happened' : 'What it was'}</span><Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></label>}
        {as === 'refund_in' && (
          refunds.length
            ? <label style={lab}><span>{req}The refund</span><Select value={refundId ?? undefined} onChange={setRefundId} placeholder="Choose" options={refunds.map((r) => ({ value: r.id, label: `${r.on} · EGP ${money(r.amount)}${r.reference ? ` · ${r.reference}` : ''}` }))} /></label>
            : <Alert type="warning" showIcon message="No refund into ADIB is waiting on Returns & credits. Settle the return as a refund there first." />
        )}
      </div>
    </Modal>
  );
};

export const DifferenceWindow: React.FC<{ line: BankRow | null; onClose: () => void; onSaved: () => void }> = ({ line, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const [reason, setReason] = useState<'fee' | 'other_po' | 'corrected' | 'other' | 'split' | null>(null);
  const [note, setNote] = useState('');
  const [pos, setPos] = useState<VendorPo[] | null>(null);
  const [addPo, setAddPo] = useState<number | null>(null);
  const [split, setSplit] = useState<SplitCandidates | null>(null);
  const [other, setOther] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (line) { setReason(null); setNote(''); setPos(null); setAddPo(null); setSplit(null); setOther(null); } }, [line]);
  if (!line || !line.payment) return null;
  const p = line.payment;
  const diff = line.difference || 0;
  const pick = (r: typeof reason) => {
    setReason(r);
    if (r === 'other_po' && pos === null && p.vendor) getVendorPos(p.vendor.id).then((list) => setPos(list.filter((x) => !p.pos.includes(x.poId)))).catch(() => setPos([]));
    if (r === 'split' && split === null) {
      getSplitCandidates(line.id)
        .then((c) => { setSplit(c); const best = c.lines.find((x) => x.fit !== 'differs'); if (best) setOther(best.id); })
        .catch((e) => { message.error(e?.response?.data?.error || 'Could not load the bank lines'); setSplit({ payment: { id: p.id, code: p.code, amount: p.amount }, part: line.debit, partCharge: 0, rest: 0, lines: [] }); });
    }
  };
  const ok = !!reason && (reason !== 'other' || !!note.trim()) && (reason !== 'other_po' || !!addPo) && (reason !== 'split' || !!other);
  // B1: for an InstaPay transfer the right amount is what the bank took less its charge.
  const instapay = line.kind === 'transfer_out' && p.method === 'instapay';
  const net = instapay ? withoutInstapayCharge(line.debit) : null;
  const fitText = (f: 'exact' | 'charge' | 'differs', dx: number) => (f === 'exact' ? 'exactly the rest' : f === 'charge' ? `the rest + ${money(dx)} InstaPay charge` : `${dx > 0 ? '+' : '−'}${money(Math.abs(dx))} from the rest`);
  const save = async () => {
    if (!reason) return;
    setSaving(true);
    try {
      await explainBankDifference(line.id, { reason, note: note.trim() || undefined, addPoId: reason === 'other_po' ? addPo ?? undefined : undefined, otherLineId: reason === 'split' ? other ?? undefined : undefined });
      message.success(reason === 'split' ? 'Payment split over the two transfers' : 'Difference explained');
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };
  const corrected = { value: 'corrected' as const, label: 'I recorded the wrong amount', hint: net != null
    ? `Correct the payment to ${money(net)}: the bank's ${money(line.debit)} less the ${money(line.debit - net)} InstaPay charge (the first entry is kept in its history).`
    : `Correct the payment to ${money(line.debit)} (the first entry is kept in its history).` };
  const other_ = { value: 'other' as const, label: 'Something else', hint: 'Write what happened; it goes to Ahmed on the settlement.' };
  // The bank took more: a charge, another PO, a wrong amount. The bank took less (B3): more than one transfer, a wrong amount.
  const options = diff < 0 ? [
    { value: 'split' as const, label: 'Paid in more than one transfer', hint: `This transfer is part of ${p.code}; the rest went in another transfer. The payment is split in two and the rest is paired with that line (the first entry is kept in its history).` },
    corrected, other_,
  ] : [
    { value: 'fee' as const, label: 'Bank or InstaPay charge', hint: `${money(diff)} is booked as a bank fee for the week.` },
    { value: 'other_po' as const, label: 'It also paid another PO', hint: 'Add that PO to this payment.' },
    corrected, other_,
  ];
  return (
    <Modal open={!!line} title={<div style={{ paddingInlineEnd: 28 }}>Explain the difference · bank {money(line.debit)} vs recorded {money(p.amount)} on {p.pos.join(', ') || p.code}</div>}
      onCancel={onClose} onOk={save} okText="Save" okButtonProps={{ disabled: !ok, loading: saving }} maskClosable={false} width={compact ? 520 : 680} destroyOnClose>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div id="diff-why" style={{ fontWeight: 600 }}>{req}Why are they different?</div>
        <div role="radiogroup" aria-labelledby="diff-why" style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {options.map((o) => <OutcomeCard key={o.value} label={o.label} on={reason === o.value} onPick={() => pick(o.value)}><div style={text}>{o.hint}</div></OutcomeCard>)}
        </div>
        {reason === 'other_po' && (
          pos === null ? null : pos.length
            ? <label style={lab}><span>{req}The other PO of <bdi>{p.vendor?.name}</bdi> ({money(diff)} goes to it)</span><Select value={addPo ?? undefined} onChange={setAddPo} placeholder="Choose" options={pos.map((x) => ({ value: x.id, label: `${x.poId} · ${x.status} · EGP ${money(x.value - x.paid)} unpaid` }))} /></label>
            : <Alert type="warning" showIcon message="No other unpaid PO of this vendor." />
        )}
        {reason === 'split' && split && (
          split.lines.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={text}>
                This line is <b>{money(split.part)}</b>{split.partCharge > 0.005 ? ` + ${money(split.partCharge)} InstaPay charge` : ''} of {split.payment.code} {money(split.payment.amount)}: the rest is <b>{money(split.rest)}</b>.
              </div>
              <label style={lab}><span>{req}The other transfer (the rest)</span>
                <Select value={other ?? undefined} onChange={setOther} placeholder="Choose the bank line"
                  options={split.lines.map((x) => ({ value: x.id, label: `${new Date(`${x.date}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })} · ${money(x.debit)} · ${fitText(x.fit, x.difference)} · ${x.narrative}` }))} />
              </label>
              {other && split.lines.find((x) => x.id === other)?.fit === 'differs' && (
                <Alert type="info" showIcon message="That line is not exactly the rest: after the split it still shows its own difference to explain." />
              )}
            </div>
          ) : <Alert type="warning" showIcon message="No other InstaPay or card line within 7 days is free to be the rest. If the rest is on next week's statement, explain this line after loading it." />
        )}
        {(reason === 'other' || reason === 'fee' || reason === 'corrected' || reason === 'other_po' || reason === 'split') && (
          <label style={lab}><span>{reason === 'other' ? req : ''}{reason === 'other' ? 'What happened' : 'Note'}</span><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === 'other' ? 'Required' : 'Optional'} maxLength={500} /></label>
        )}
      </div>
    </Modal>
  );
};
